/**
 * useGlobalRevisions - Custom hook for global revisions state management
 * Following modular architecture pattern from existing dashboard hooks
 */

import { useState, useEffect, useCallback } from 'react';
import GlobalRevisionsService from '../../../utils/globalRevisionsService.js';

export function useGlobalRevisions(options = {}) {
    const {
        autoFetch = true,
        testMode = true,
        initialLimit = 10
    } = options;

    // State for recent revisions (dashboard display)
    const [recentRevisions, setRecentRevisions] = useState([]);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [recentStats, setRecentStats] = useState({});
    const [loadingRecent, setLoadingRecent] = useState(false);
    const [recentError, setRecentError] = useState(null);

    // State for all revisions (modal display)
    const [allRevisions, setAllRevisions] = useState([]);
    const [allStats, setAllStats] = useState({});
    const [pagination, setPagination] = useState({ hasMore: false });
    const [loadingAll, setLoadingAll] = useState(false);
    const [allError, setAllError] = useState(null);

    // State for filters and search
    const [filters, setFilters] = useState({
        search: '',
        activeOnly: true,
        dateFrom: null,
        dateTo: null
    });

    // State for modal
    const [isModalOpen, setIsModalOpen] = useState(false);

    /**
     * Fetch recent revisions for dashboard display
     */
    const fetchRecentRevisions = useCallback(async (limit = initialLimit, forceTestMode = testMode) => {
        setLoadingRecent(true);
        setRecentError(null);

        try {
            const result = await GlobalRevisionsService.getRecentRevisions(limit, forceTestMode);

            if (result.success) {
                setRecentRevisions(result.revisions);
                setSelectedWeek(result.selectedWeek);
                setRecentStats(result.stats);
            } else {
                setRecentError(result.error);
                setRecentRevisions([]);
            }
        } catch (error) {
            console.error('Error in fetchRecentRevisions:', error);
            setRecentError(error.message);
            setRecentRevisions([]);
        } finally {
            setLoadingRecent(false);
        }
    }, [initialLimit, testMode]);

    /**
     * Fetch all revisions with current filters for modal display
     */
    const fetchAllRevisions = useCallback(async (offset = 0, append = false) => {
        setLoadingAll(true);
        setAllError(null);

        try {
            const result = await GlobalRevisionsService.getAllRevisions({
                limit: 20,
                offset,
                ...filters
            });

            if (result.success) {
                setAllRevisions(prev => append ? [...prev, ...result.revisions] : result.revisions);
                setPagination(result.pagination);
                setAllStats(result.stats || {});
            } else {
                setAllError(result.error);
                if (!append) {
                    setAllRevisions([]);
                }
            }
        } catch (error) {
            console.error('Error in fetchAllRevisions:', error);
            setAllError(error.message);
            if (!append) {
                setAllRevisions([]);
            }
        } finally {
            setLoadingAll(false);
        }
    }, [filters]);

    /**
     * Load more revisions for pagination
     */
    const loadMoreRevisions = useCallback(() => {
        if (!loadingAll && pagination.hasMore) {
            fetchAllRevisions(allRevisions.length, true);
        }
    }, [fetchAllRevisions, loadingAll, pagination.hasMore, allRevisions.length]);

    /**
     * Refresh recent revisions
     */
    const refreshRecent = useCallback(() => {
        fetchRecentRevisions();
    }, [fetchRecentRevisions]);

    /**
     * Update filters and refresh data
     */
    const updateFilters = useCallback((newFilters) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
        // Reset pagination when filters change
        setAllRevisions([]);
        setPagination({ hasMore: false });
    }, []);

    /**
     * Search revisions
     */
    const searchRevisions = useCallback((searchTerm) => {
        updateFilters({ search: searchTerm });
    }, [updateFilters]);

    /**
     * Toggle modal visibility
     */
    const toggleModal = useCallback(() => {
        setIsModalOpen(prev => !prev);

        // Fetch all revisions when opening modal for the first time
        if (!isModalOpen && allRevisions.length === 0) {
            fetchAllRevisions();
        }
    }, [isModalOpen, allRevisions.length, fetchAllRevisions]);

    /**
     * Close modal
     */
    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    /**
     * Open modal
     */
    const openModal = useCallback(() => {
        setIsModalOpen(true);

        // Fetch all revisions when opening modal
        if (allRevisions.length === 0) {
            fetchAllRevisions();
        }
    }, [allRevisions.length, fetchAllRevisions]);

    /**
     * Get grouped revisions by client
     */
    const getGroupedRevisions = useCallback((revisionList = recentRevisions) => {
        return GlobalRevisionsService.groupRevisionsByClient(revisionList);
    }, [recentRevisions]);

    /**
     * Check if there are any recent revisions to show
     */
    const hasRecentRevisions = recentRevisions.length > 0;

    /**
     * Check if should show the banner/section
     */
    const shouldShowSection = hasRecentRevisions && !loadingRecent;

    // Auto-fetch recent revisions on mount
    useEffect(() => {
        if (autoFetch) {
            fetchRecentRevisions();
        }
    }, [autoFetch, fetchRecentRevisions]);

    // Fetch all revisions when filters change
    useEffect(() => {
        if (isModalOpen) {
            fetchAllRevisions();
        }
    }, [filters, isModalOpen]);

    return {
        // Recent revisions (dashboard)
        recentRevisions,
        selectedWeek,
        recentStats,
        loadingRecent,
        recentError,
        hasRecentRevisions,
        shouldShowSection,

        // All revisions (modal)
        allRevisions,
        allStats,
        pagination,
        loadingAll,
        allError,

        // Filters and search
        filters,
        updateFilters,
        searchRevisions,

        // Modal state
        isModalOpen,
        toggleModal,
        closeModal,
        openModal,

        // Actions
        fetchRecentRevisions,
        fetchAllRevisions,
        loadMoreRevisions,
        refreshRecent,
        getGroupedRevisions,

        // Computed values
        testMode,
        totalRecentRevisions: recentRevisions.length,
        hasMoreRevisions: pagination.hasMore
    };
}

/**
 * Simple hook for just fetching stats
 */
export function useGlobalRevisionsStats() {
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchStats = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const result = await GlobalRevisionsService.getStats();

            if (result.success) {
                setStats(result.stats);
            } else {
                setError(result.error);
            }
        } catch (error) {
            console.error('Error fetching global revisions stats:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    return {
        stats,
        loading,
        error,
        refresh: fetchStats
    };
}
